import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/http/client'
import { captureSurveySubmitted } from '@/lib/analytics'
import type { ApiResponse } from '@/api/http/types'
import { queryKeys } from '@/api/query-keys'
import type { AvailableSurveyApiItem } from '@/features/home/api/available-surveys-queries'
import type { SurveyAnswerPayload } from '../lib/survey-answer'
import { SpSurveyPath } from '../lib/sp-survey-paths'
import { isPendingSurveyUser } from '../lib/pending-survey-session'
import { useSessionStore } from '@/stores/useSessionStore'
import type {
  ProviderNextResult,
  ProviderResponseState,
  ProviderSubmitConfirmation,
} from '../lib/types'

export interface SaveSurveyAnswerInput {
  instrumentId: string
  responseId: string
  payload: SurveyAnswerPayload
}

export function useSaveSurveyAnswer() {
  const queryClient = useQueryClient()
  const pendingUser = useSessionStore((s) => isPendingSurveyUser(s.user))

  return useMutation({
    mutationFn: async ({ responseId, payload }: SaveSurveyAnswerInput) => {
      const { data } = await apiClient.patch<ApiResponse<ProviderNextResult>>(
        pendingUser
          ? SpSurveyPath.pendingSaveAnswer(responseId)
          : SpSurveyPath.saveAnswer(responseId),
        payload
      )
      if (!data.data) {
        throw new Error('Save answer failed')
      }
      return data.data
    },
    onSuccess: (result, { instrumentId, responseId }) => {
      queryClient.setQueryData<{
        response: ProviderResponseState
        definition: unknown
        resumed: boolean
      }>(queryKeys.spSurvey.session(instrumentId, responseId), (prev) => {
        if (!prev) return prev
        return {
          ...prev,
          response: {
            ...prev.response,
            currentQuestionId: result.nextQuestionId,
          },
        }
      })
    },
  })
}

export interface SubmitSurveyInput {
  instrumentId: string
  responseId: string
}

export function useSubmitSurvey() {
  const queryClient = useQueryClient()
  const pendingUser = useSessionStore((s) => isPendingSurveyUser(s.user))

  return useMutation({
    mutationFn: async ({ responseId }: SubmitSurveyInput) => {
      const { data } = pendingUser
        ? await apiClient.post<ApiResponse<ProviderSubmitConfirmation>>(
            SpSurveyPath.pendingRetain(responseId),
          )
        : await apiClient.post<ApiResponse<ProviderSubmitConfirmation>>(
            SpSurveyPath.responseSubmit(responseId),
            { submissionClient: 'web' },
          )
      if (!data.data) {
        throw new Error('Submit survey failed')
      }
      return data.data
    },
    onSuccess: (confirmation, { instrumentId, responseId }) => {
      captureSurveySubmitted({
        surveyId: instrumentId,
        responseId,
        rewardAmount: confirmation?.rewardEnabled ? confirmation.rewardAmount : null,
      })
      queryClient.setQueryData<AvailableSurveyApiItem[]>(
        queryKeys.spInstrument.availableList(),
        (prev) =>
          prev?.filter((survey) => survey.instrumentId !== instrumentId) ?? prev
      )
      queryClient.invalidateQueries({
        queryKey: queryKeys.spInstrument.status(instrumentId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.spSurveyHistory.all(),
      })
      // Refresh home dashboard: remove completed card + update metrics
      queryClient.invalidateQueries({
        queryKey: queryKeys.spInstrument.availableList(),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.spProfile.stats(),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.spRewards.summary(),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.spNotifications.all(),
      })
    },
  })
}

export function useAbandonSurvey() {
  const pendingUser = useSessionStore((s) => isPendingSurveyUser(s.user))
  return useMutation({
    mutationFn: async ({
      responseId,
      reason,
    }: {
      responseId: string
      reason: 'browser_closed' | 'app_closed'
    }) => {
      await apiClient.post(
        pendingUser
          ? SpSurveyPath.pendingAbandon(responseId)
          : SpSurveyPath.responseAbandon(responseId),
        { reason },
      )
    },
  })
}

export function cacheSurveySession(
  queryClient: ReturnType<typeof useQueryClient>,
  instrumentId: string,
  responseId: string,
  session: {
    response: ProviderResponseState
    definition: unknown
    resumed: boolean
  }
) {
  queryClient.setQueryData(
    queryKeys.spSurvey.session(instrumentId, responseId),
    session
  )
}
